import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, MessagesSquare } from 'lucide-react';
import { listParticipantThreads, ParticipantThreadSummary } from '../../services/participantThreads';
import { ParticipantThreadView } from './ParticipantThreadView';
import { ThreadCandidatesReview } from './ThreadCandidatesReview';

interface ParticipantThreadsPanelProps {
  sessionId: string;
}

const formatRelative = (timestamp: string) => {
  try {
    return new Date(timestamp).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return timestamp;
  }
};

export const ParticipantThreadsPanel: React.FC<ParticipantThreadsPanelProps> = ({ sessionId }) => {
  const [threads, setThreads] = useState<ParticipantThreadSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listParticipantThreads(sessionId)
      .then(t => { if (!cancelled) setThreads(t); })
      .catch(() => { if (!cancelled) setError('Failed to load participant threads'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sessionId]);

  // Group threads by participant so each participant shows their avatar threads together.
  const byParticipant = useMemo(() => {
    const groups = new Map<string, { participantName: string; threads: ParticipantThreadSummary[] }>();
    for (const t of threads) {
      const key = t.participantId;
      if (!groups.has(key)) groups.set(key, { participantName: t.participantName, threads: [] });
      groups.get(key)!.threads.push(t);
    }
    return Array.from(groups.entries());
  }, [threads]);

  const selected = threads.find(t => `${t.avatarId}::${t.participantId}` === selectedKey) || null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        <Loader2 size={24} className="animate-spin" />
      </div>
    );
  }

  if (error) {
    return <div className="text-sm text-red-600 p-6">{error}</div>;
  }

  if (threads.length === 0) {
    return (
      <div className="rounded-xl bg-white p-12 shadow-sm border border-slate-200 text-center m-6">
        <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
          <MessagesSquare size={28} className="text-slate-400" />
        </div>
        <p className="text-slate-500 text-sm">No participant conversations yet.</p>
        <p className="text-slate-400 text-xs mt-2">
          Threads appear here once a participant chats with an avatar from the mobile view.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full">
      <div className="w-80 shrink-0 border-r border-slate-200 overflow-y-auto">
        {byParticipant.map(([participantId, group]) => (
          <div key={participantId} className="border-b border-slate-100">
            <div className="px-4 py-2 text-[11px] font-bold text-slate-500 uppercase tracking-wide bg-slate-50">
              {group.participantName}
            </div>
            {group.threads.map(t => {
              const key = `${t.avatarId}::${t.participantId}`;
              const isSelected = key === selectedKey;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedKey(key)}
                  className={`w-full text-left px-4 py-3 border-l-4 transition-colors ${
                    isSelected ? 'border-primary bg-primary/5' : 'border-transparent hover:bg-slate-50'
                  }`}
                >
                  <div className="text-sm font-semibold text-navy truncate">{t.avatarName}</div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    {t.messageCount} message{t.messageCount === 1 ? '' : 's'} · {formatRelative(t.lastMessageAt)}
                  </div>
                </button>
              );
            })}
          </div>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto">
        {selected ? (
          <>
            <div className="px-4 py-3 border-b border-slate-200 bg-white sticky top-0">
              <h3 className="text-sm font-bold text-navy">{selected.participantName} ↔ {selected.avatarName}</h3>
            </div>
            <ParticipantThreadView
              sessionId={sessionId}
              avatarId={selected.avatarId}
              participantId={selected.participantId}
              avatarName={selected.avatarName}
              participantName={selected.participantName}
            />
            <ThreadCandidatesReview
              key={selectedKey}
              sessionId={sessionId}
              avatarId={selected.avatarId}
              participantId={selected.participantId}
            />
          </>
        ) : (
          <div className="flex items-center justify-center h-full text-sm text-slate-400">
            Select a conversation to view it
          </div>
        )}
      </div>
    </div>
  );
};

export default ParticipantThreadsPanel;
